import React from 'react';
import { resourceColors } from '@cotera/client/app/v1/resources/type/resource-type-colors';

/**
 * The colour surfaces of a dataset page, as Tailwind class strings.
 *
 * Every value is applied through `cn()`, so a caller can override one slot
 * without restating the rest, and any Tailwind class is fair game — a slot is
 * not limited to a background or a text colour.
 */
export type DatasetTheme = {
  /** Band holding the name, subtitle and icon. */
  headerBackground: string;
  /** Chip the dataset icon sits in. */
  headerIcon: string;
  /** The dataset name. */
  title: string;
  /** The line under the name. */
  subtitle: string;
  /** Scrolling body the grid and empty state sit in. */
  mainBody: string;
  /** Card surface shared by the grid and the empty state. */
  surface: string;
  /** Border of that card surface. */
  surfaceBorder: string;
  /**
   * Corner radius of that card surface. Flattened where the dataset fills a
   * container of its own — an artifact panel — and the rounding would read as
   * a card floating inside a square hole.
   */
  surfaceRadius: string;
  /**
   * Which edges the grid's card draws. A card on a page is closed on all four;
   * a grid filling an artifact panel only needs the line separating it from
   * what sits above.
   */
  gridEdges: string;
  /** Grid chrome: the top bar and the footer. */
  gridChrome: string;
};

/**
 * White-oriented for the dataset itself: the grid, its chrome and the cards
 * around it are solid white, so the data reads as a sheet sitting on the page
 * rather than blending into it. The page around them — its background, the
 * header band, the title — is left to the app, which is why those slots are
 * empty by default.
 */
export const DEFAULT_DATASET_THEME: DatasetTheme = {
  headerBackground: '',
  headerIcon: `${resourceColors.dataset.bg} ${resourceColors.dataset.text}`,
  title: '',
  subtitle: 'text-muted-foreground',
  mainBody: '',
  surface: 'bg-white',
  surfaceBorder: 'border-slate-200',
  surfaceRadius: 'rounded-lg',
  gridEdges: 'border',
  gridChrome: 'bg-white',
};

const DatasetThemeContext = React.createContext<DatasetTheme>(
  DEFAULT_DATASET_THEME
);

/**
 * Themes everything under it. Partial, so a caller states only the slots it
 * cares about; the rest stay on the default.
 */
export const DatasetThemeProvider: React.FC<{
  theme?: Partial<DatasetTheme>;
  children: React.ReactNode;
}> = ({ theme, children }) => {
  const value = React.useMemo(
    () => ({ ...DEFAULT_DATASET_THEME, ...theme }),
    [theme]
  );
  return (
    <DatasetThemeContext.Provider value={value}>
      {children}
    </DatasetThemeContext.Provider>
  );
};

export const useDatasetTheme = (): DatasetTheme =>
  React.useContext(DatasetThemeContext);
